import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtRange, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ChevronDown, ChevronUp, Wrench, Info, ArrowLeft, CalendarCheck, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";

const PRIO = {
  Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700",
  Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600",
};

function ConsultationDialog({ open, onClose, service, business, scanId }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", preferred_time: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please enter your name and email."); return; }
    setBusy(true);
    try {
      await api.post("/consultations", {
        ...form,
        service_id: service?.service_id || null,
        service_name: service?.name || null,
        business_name: business?.name || null,
        scan_id: scanId || null,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const close = () => { setDone(false); setForm({ name: "", email: "", phone: "", message: "", preferred_time: "" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="consult-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="consult-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h3 className="font-heading text-lg font-semibold text-slate-900 mt-3">Request received</h3>
            <p className="text-sm text-slate-600 mt-1">Thanks, {form.name || "there"} — we've logged your request{service?.name ? ` about ${service.name}` : ""} and our team will reach out shortly.</p>
            <Button onClick={close} data-testid="consult-close" className="mt-4 bg-slate-900 hover:bg-slate-800">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Request a consultation{service?.name ? `: ${service.name}` : ""}</DialogTitle>
              <DialogDescription>Tell us how to reach you and we'll follow up — no obligation.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Input placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="consult-name" />
              <Input type="email" placeholder="Your email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="consult-email" />
              <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="consult-phone" />
              <Input placeholder="Best time to reach you (optional)" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} data-testid="consult-time" />
              <Textarea rows={3} placeholder="Anything you'd like us to know? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="consult-message" />
              <Button onClick={submit} disabled={busy} data-testid="consult-submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SolutionCard({ s, checked, onToggle, onConsult, onAddOne, adding }) {
  const [open, setOpen] = useState(false);

  // Honest "No direct service recommendation" card — no pricing, no selection.
  if (s.no_service) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5" data-testid={`solution-${s.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="font-heading font-semibold text-slate-700">{s.problem}</span>
            <span className="block text-sm text-slate-500 mt-0.5">No direct service recommendation</span>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO[s.priority] || PRIO.Low}`}>{s.priority}</span>
        </div>
        <p className="mt-3 text-sm text-slate-600" data-testid={`solution-noservice-reason-${s.id}`}>{s.reason}</p>
      </div>
    );
  }

  const p = s.pricing || {};
  const mo = s.modeled_opportunity || {};
  const cur = p.currency || "USD";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`solution-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <label className="flex items-start gap-3 min-w-0 cursor-pointer">
          <input type="checkbox" checked={checked} onChange={() => onToggle(s.id)} data-testid={`solution-select-${s.id}`}
            className="mt-1.5 w-4 h-4 accent-indigo-600" />
          <span className="min-w-0">
            <span className="font-heading font-semibold text-slate-900">{s.name}</span>
            <span className="block text-sm text-slate-500 mt-0.5">{s.problem}</span>
          </span>
        </label>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO[s.priority] || PRIO.Low}`}>{s.priority}</span>
          {s.recurring_service && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase bg-indigo-50 text-indigo-600 border border-indigo-200">Ongoing</span>}
          {s.needs_review && <span data-testid={`solution-provisional-${s.id}`} className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300">Provisional</span>}
        </div>
      </div>

      {s.client_facing_description && <p className="mt-2 text-sm text-slate-600">{s.client_facing_description}</p>}

      {s.needs_review && s.review_note && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid={`solution-review-note-${s.id}`}>
          <b>Pending review:</b> {s.review_note}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Estimated investment</div>
          <div className="font-semibold text-slate-900" data-testid={`solution-price-${s.id}`}>{fmtRange(p.price_range, cur)}</div>
          <div className="text-[10px] text-amber-600 font-semibold">{s.needs_review ? "PROVISIONAL — PENDING REVIEW" : "AI-ASSISTED ESTIMATE"}</div></div>
        <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Timeline</div>
          <div className="text-slate-700">{s.timeframe || "—"}</div></div>
        {p.recurring && <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Ongoing</div>
          <div className="text-slate-700">{fmtMoney(p.recurring.client_price, cur)}/mo</div></div>}
        {mo.available && <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Modeled opportunity</div>
          <div className="text-emerald-700 font-medium">~{fmtMoney(mo.annual, cur)}/yr</div>
          <div className="text-[10px] text-slate-400">estimate, not guaranteed</div></div>}
      </div>

      {s.expected_outcome && (
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-800" data-testid={`solution-outcome-${s.id}`}>
          <span className="font-semibold">Expected outcome:</span> {s.expected_outcome}
        </div>
      )}

      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`solution-toggle-${s.id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600">
        <Wrench className="w-4 h-4" /> View service {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-slate-400">Scope, deliverables &amp; investment shown above before you proceed.</span>
        <Button type="button" size="sm" onClick={() => onAddOne(s)} disabled={adding}
          data-testid={`solution-addone-${s.id}`}
          className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Add to proposal</>}
        </Button>
      </div>

      {open && (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 text-sm" data-testid={`solution-detail-${s.id}`}>
          {s.why_it_matters && <p><span className="font-semibold text-slate-800">Why it matters:</span> <span className="text-slate-600">{s.why_it_matters}</span></p>}
          {(s.deliverables || []).length > 0 && (
            <div data-testid={`solution-deliverables-${s.id}`}><span className="font-semibold text-slate-800">What you get (deliverables):</span>
              <ul className="mt-1 list-disc list-inside text-slate-600">{s.deliverables.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          )}
          <div><span className="font-semibold text-slate-800">Scope of work:</span>
            <ul className="mt-1 list-disc list-inside text-slate-600">{(s.scope || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-500">
            <div>Confidence: <b>{s.confidence}</b></div>
            <div>Skills: {(s.skills || []).join(", ") || "—"}</div>
          </div>
          {(s.assumptions || []).length > 0 && (
            <details className="text-xs text-slate-500"><summary className="cursor-pointer font-medium">Assumptions</summary>
              <ul className="mt-1 list-disc list-inside">{s.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </details>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => onConsult(s)} data-testid={`solution-consult-${s.id}`}
            className="mt-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50">
            <CalendarCheck className="w-4 h-4 mr-1.5" /> Request a consultation
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ScanSolutions() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [consult, setConsult] = useState(null); // null=closed, {} or {service...}=open

  useEffect(() => {
    api.get(`/scan/${id}/solutions`).then(({ data }) => {
      setData(data);
      setSel(Object.fromEntries((data.solutions || []).filter((s) => !s.no_service).map((s) => [s.id, true])));
    }).catch(() => setData({ solutions: [], packages: [] }));
  }, [id]);

  const toggle = (sid) => setSel((x) => ({ ...x, [sid]: !x[sid] }));
  const chosen = Object.keys(sel).filter((k) => sel[k]);

  // Feedback shared by one-click + bulk add. Honest about what was added vs skipped as a duplicate.
  const announce = (res) => {
    const added = (res.added || []).length;
    const dupes = (res.skipped_duplicates || []).length;
    const pid = res.proposal?.public_id;
    if (added > 0) {
      toast.success(`${added} ${added === 1 ? "service" : "services"} added to your proposal${dupes ? ` (${dupes} already on it)` : ""}.`, {
        action: pid ? { label: "View proposal", onClick: () => nav(`/proposals/${pid}`) } : undefined,
      });
    } else if (dupes > 0) {
      toast.info("Already on your proposal — nothing to add.", {
        action: pid ? { label: "View proposal", onClick: () => nav(`/proposals/${pid}`) } : undefined,
      });
    }
  };

  const addOne = async (s) => {
    if (!user) { nav(`/login?redirect=/scan/${id}/solutions`); return; }
    setAddingId(s.id);
    try {
      const { data: res } = await api.post("/proposals/add-solutions", {
        scan_id: id, solution_ids: [s.id],
        business_name: data?.business?.name, business_address: data?.business?.address,
      });
      announce(res);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingId(null); }
  };

  const createProposal = async () => {
    if (!user) { nav(`/login?redirect=/scan/${id}/solutions`); return; }
    if (chosen.length === 0) { toast.error("Select at least one solution."); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/proposals/add-solutions", {
        scan_id: id, solution_ids: chosen,
        business_name: data?.business?.name, business_address: data?.business?.address,
      });
      const pid = res.proposal?.public_id;
      announce(res);
      if (pid) nav(`/proposals/${pid}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const sols = data.solutions || [];

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="scan-solutions-page">
      <Link to={`/report/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back to report</Link>
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em]">Solutions & pricing</p>
      <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">How we can fix it — for {data?.business?.name || "your business"}</h1>
      <p className="mt-2 text-slate-600 text-sm max-w-2xl">Each finding becomes a costed solution. Prices are AI-assisted estimates from configurable pricing rules — not market-verified prices. Modeled opportunity is a separate estimate of potential upside, never guaranteed revenue.</p>

      {sols.length === 0 ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 text-slate-500" data-testid="solutions-empty">
          No actionable, service-mappable findings were produced for this scan. We never invent work that isn't supported by the findings.
        </div>
      ) : (
        <>
          {(data.packages || []).length > 0 && (
            <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="solution-packages">
              {data.packages.map((pk) => (
                <div key={pk.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-heading font-semibold text-slate-900">{pk.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{pk.blurb}</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{fmtMoney(pk.totals.one_time.client_price, pk.totals.currency)}</p>
                  {pk.totals.recurring && <p className="text-xs text-slate-500">+ {fmtMoney(pk.totals.recurring.client_price, pk.totals.currency)}/mo</p>}
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 space-y-3">
            {sols.map((s) => <SolutionCard key={s.id} s={s} checked={!!sel[s.id]} onToggle={toggle} onConsult={(sv) => setConsult(sv || {})} onAddOne={addOne} adding={addingId === s.id} />)}
          </div>
          <div className="mt-6 sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 backdrop-blur p-4 shadow-lg">
            <p className="text-sm text-slate-600 flex items-center gap-1.5"><Info className="w-4 h-4 text-slate-400" /> {chosen.length} selected</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setConsult({})} data-testid="open-consult-btn" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                <CalendarCheck className="w-4 h-4 mr-1.5" /> Book a consultation
              </Button>
              <Button onClick={createProposal} disabled={busy} data-testid="build-proposal-btn" className="bg-slate-900 hover:bg-slate-800">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add selected to proposal →"}
              </Button>
            </div>
          </div>
        </>
      )}
      <ConsultationDialog open={consult !== null} onClose={() => setConsult(null)} service={consult}
        business={data?.business} scanId={id} />
    </div>
  );
}
